import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Typography, Chip, CircularProgress, IconButton, Tooltip, GlobalStyles } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { dashboardAPI } from '../services/api';
import { decodePolyline } from '../utils/polyline';

// El anillo pulsante solo se muestra cuando el móvil está "vigente" (recolectando/en movimiento)
const truckIcon = (color, vigente) => L.divIcon({
  className: 'movil-truck-icon',
  html: `<div style="position:relative;width:28px;height:28px;">
    ${vigente ? `<span style="position:absolute;inset:-6px;border-radius:50%;background:${color};opacity:0.5;animation:movilPulse 1.6s ease-out infinite;"></span>` : ''}
    <div style="position:relative;background:${color};width:28px;height:28px;border-radius:50%;
      display:flex;align-items:center;justify-content:center;border:2px solid #fff;
      box-shadow:0 1px 4px rgba(0,0,0,0.4);font-size:16px;">🚛</div>
  </div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

// Posición considerada "en vivo" si se recibió hace menos de 15 minutos
const POSICION_VIGENTE_MS = 15 * 60 * 1000;
// Referencia estable: un [] nuevo por render invalidaría los useMemo de abajo.
const SIN_ITINERARIOS = [];

const ROUTE_COLORS = ['#1976d2', '#7b1fa2', '#c2185b', '#f57c00', '#00796b', '#5d4037', '#455a64'];

const ESTADO_COLOR = {
  recolectado: '#2e7d32',
  pendiente: '#f57c00',
  anulado: '#9e9e9e',
};

const ESTADO_LABEL = {
  recolectado: 'Recolectado',
  pendiente: 'Pendiente',
  anulado: 'Anulado',
};

const defaultCenter = [-31.4201, -64.1888]; // Córdoba, Argentina

function estadoDeRecoleccion(r) {
  if (r.FechaAnulado) return 'anulado';
  if (r.FechaRetiro) return 'recolectado';
  return 'pendiente';
}

function formatFecha(fecha) {
  if (!fecha) return '-';
  const [y, m, d] = fecha.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

function FitBounds({ points }) {
  const map = useMap();

  useEffect(() => {
    if (points.length > 0) {
      map.fitBounds(points, { padding: [40, 40] });
    }
  }, [points, map]);

  return null;
}

// Leaflet necesita recalcular su tamaño cuando el contenedor cambia (ej. al entrar/salir de pantalla completa)
function InvalidateSizeOnChange({ trigger }) {
  const map = useMap();

  useEffect(() => {
    const id = setTimeout(() => map.invalidateSize(), 100);
    return () => clearTimeout(id);
  }, [trigger, map]);

  return null;
}

export default function DashboardMapHoy() {
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [fullscreen]);

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-mapa-hoy'],
    queryFn: async () => {
      const res = await dashboardAPI.mapaHoy();
      return res.data.data;
    },
    refetchInterval: 60000,
  });

  const itinerarios = data?.itinerarios ?? SIN_ITINERARIOS;

  const moviles = useMemo(() => {
    const ahora = Date.now();
    return itinerarios
      .map((it, idx) => ({ ...it, colorIdx: idx }))
      .filter((it) => it.Lat_Actual != null && it.Lng_Actual != null)
      .map((it) => ({
        ...it,
        vigente: it.Fecha_Posicion
          ? ahora - new Date(it.Fecha_Posicion).getTime() < POSICION_VIGENTE_MS
          : false,
      }));
  }, [itinerarios]);

  const allPoints = useMemo(() => {
    const pts = [];
    itinerarios.forEach((it) => {
      (it.recolecciones || []).forEach((r) => pts.push([r.Latitud, r.Longitud]));
    });
    moviles.forEach((m) => pts.push([m.Lat_Actual, m.Lng_Actual]));
    return pts;
  }, [itinerarios, moviles]);

  const totales = useMemo(() => {
    const acc = { recolectado: 0, pendiente: 0, anulado: 0 };
    itinerarios.forEach((it) => {
      (it.recolecciones || []).forEach((r) => {
        acc[estadoDeRecoleccion(r)] += 1;
      });
    });
    return acc;
  }, [itinerarios]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (itinerarios.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        No hay itinerarios generados para hoy
      </Typography>
    );
  }

  return (
    <Box>
      <GlobalStyles
        styles={{
          '@keyframes movilPulse': {
            '0%': { transform: 'scale(0.6)', opacity: 0.7 },
            '100%': { transform: 'scale(1.8)', opacity: 0 },
          },
        }}
      />

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip label={`Recolectado: ${totales.recolectado}`} size="small" sx={{ bgcolor: ESTADO_COLOR.recolectado, color: '#fff' }} />
        <Chip label={`Pendiente: ${totales.pendiente}`} size="small" sx={{ bgcolor: ESTADO_COLOR.pendiente, color: '#fff' }} />
        {totales.anulado > 0 && (
          <Chip label={`Anulado: ${totales.anulado}`} size="small" sx={{ bgcolor: ESTADO_COLOR.anulado, color: '#fff' }} />
        )}
        {itinerarios.map((it, idx) => (
          <Chip
            key={it.Lote}
            label={`${it.NombreMovil || it.Dominio || `Móvil ${it.Movil}`}${it.Atrasado ? ` (atrasado ${formatFecha(it.Fecha)})` : ''}`}
            size="small"
            variant="outlined"
            sx={{
              borderColor: it.Atrasado ? '#d32f2f' : ROUTE_COLORS[idx % ROUTE_COLORS.length],
              color: it.Atrasado ? '#d32f2f' : ROUTE_COLORS[idx % ROUTE_COLORS.length],
            }}
          />
        ))}

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title={fullscreen ? 'Salir de pantalla completa' : 'Expandir mapa'}>
          <IconButton size="small" onClick={() => setFullscreen((v) => !v)}>
            {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={
          fullscreen
            ? {
                position: 'fixed',
                inset: 0,
                zIndex: 1300,
                bgcolor: 'background.default',
                p: 2,
              }
            : { height: 500, width: '100%', borderRadius: 1, overflow: 'hidden' }
        }
      >
        {fullscreen && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Tooltip title="Salir de pantalla completa">
              <IconButton onClick={() => setFullscreen(false)}>
                <FullscreenExitIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <Box sx={{ height: fullscreen ? 'calc(100% - 48px)' : '100%', width: '100%', borderRadius: 1, overflow: 'hidden' }}>
        <MapContainer
          center={allPoints[0] || defaultCenter}
          zoom={12}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <FitBounds points={allPoints} />
          <InvalidateSizeOnChange trigger={fullscreen} />

          {itinerarios.map((it, idx) => (
            <Polyline
              key={it.Lote}
              positions={decodePolyline(it.Recorrido)}
              pathOptions={{
                color: it.Atrasado ? '#d32f2f' : ROUTE_COLORS[idx % ROUTE_COLORS.length],
                weight: 4,
                opacity: 0.6,
                dashArray: it.Atrasado ? '8 6' : null,
              }}
            />
          ))}

          {moviles.map((m) => (
            <Marker
              key={`movil-${m.Lote}`}
              position={[m.Lat_Actual, m.Lng_Actual]}
              icon={truckIcon(m.Atrasado ? '#d32f2f' : ROUTE_COLORS[m.colorIdx % ROUTE_COLORS.length], m.vigente)}
              opacity={m.vigente ? 1 : 0.5}
            >
              <Popup>
                <strong>{m.NombreMovil || m.Dominio || `Móvil ${m.Movil}`}</strong><br />
                {m.Atrasado && <><span style={{ color: '#d32f2f' }}>Itinerario atrasado ({formatFecha(m.Fecha)})</span><br /></>}
                {m.vigente ? 'En movimiento' : 'Última posición conocida'}<br />
                {m.Fecha_Posicion && new Date(m.Fecha_Posicion).toLocaleString('es-AR')}
              </Popup>
            </Marker>
          ))}

          {itinerarios.flatMap((it) =>
            (it.recolecciones || []).map((r) => {
              const estado = estadoDeRecoleccion(r);
              return (
                <CircleMarker
                  key={r.Id}
                  center={[r.Latitud, r.Longitud]}
                  radius={8}
                  pathOptions={{
                    fillColor: ESTADO_COLOR[estado],
                    fillOpacity: 0.9,
                    color: '#fff',
                    weight: 2,
                  }}
                >
                  <Popup>
                    <strong>{r.Nombre}</strong><br />
                    Estado: {ESTADO_LABEL[estado]}<br />
                    Volumen: {r.Volumen} L
                  </Popup>
                </CircleMarker>
              );
            })
          )}
        </MapContainer>
        </Box>
      </Box>
    </Box>
  );
}
